import type { CartItem } from '../contexts/CartContext';
import { getApiBaseUrl } from '../config/apiConfig';
import { clearUserSession, getCurrentToken, getCurrentUser } from './userStorage';

const PENDING_AUTH_INTENT_KEY = 'peekpass-pending-auth-intent';

export type PendingAuthIntent = {
  type: 'cart-checkout' | 'direct-checkout';
  returnTo: string;
  createdAt: string;
  eventId?: string;
  ticketTierId?: string;
};

export class CheckoutAuthRequiredError extends Error {
  result?: any;

  constructor(message = 'Necesitás iniciar sesión para continuar con la compra.', result?: any) {
    super(message);
    this.name = 'CheckoutAuthRequiredError';
    this.result = result;
  }
}

export class CheckoutRevalidationError extends Error {
  result: any;

  constructor(result: any) {
    super(result?.message || result?.error || 'La información de la compra cambió.');
    this.name = 'CheckoutRevalidationError';
    this.result = result;
  }
}

export const savePendingAuthIntent = (intent: Omit<PendingAuthIntent, 'createdAt'>) => {
  localStorage.setItem(PENDING_AUTH_INTENT_KEY, JSON.stringify({
    ...intent,
    createdAt: new Date().toISOString()
  }));
};

export const getPendingAuthIntent = (): PendingAuthIntent | null => {
  try {
    const raw = localStorage.getItem(PENDING_AUTH_INTENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const consumePendingAuthIntent = (): PendingAuthIntent | null => {
  const intent = getPendingAuthIntent();
  localStorage.removeItem(PENDING_AUTH_INTENT_KEY);
  return intent;
};

export const redirectToCheckoutAuth = (intent: Omit<PendingAuthIntent, 'createdAt'>, mode: 'login' | 'register' = 'login') => {
  savePendingAuthIntent(intent);
  window.location.href = `/?auth=${mode}`;
};

export const buildCheckoutItems = (items: CartItem[]) => items.map((item) => ({
  id: item.id,
  eventId: item.eventId || item.courseId || item.id.split(':')[0],
  ticketTierId: item.ticketTierId || (item.id.includes(':') ? item.id.split(':').slice(1).join(':') : undefined),
  ticketTierName: item.ticketTierName,
  serviceFeeRate: item.serviceFeeRate,
  title: item.ticketTierName ? `${item.title} · ${item.ticketTierName}` : item.title,
  description: item.description || `Entrada ${item.category}`,
  quantity: item.quantity || 1,
  unit_price: item.price,
  category_id: item.category
}));

const isAuthError = (status: number, result: any) => (
  status === 401 ||
  status === 403 ||
  ['TOKEN_EXPIRED', 'INVALID_TOKEN', 'NO_TOKEN', 'USER_NOT_FOUND'].includes(String(result?.code || ''))
);

const getCheckoutErrorMessage = (result: any) => {
  if (result?.code === 'MERCADOPAGO_RECONNECT_REQUIRED' || result?.requiresOrganizerSetup) {
    return result?.message || result?.error || 'El organizador debe reconectar Mercado Pago antes de vender este evento.';
  }

  return result?.message || result?.error || 'No pudimos crear el pago.';
};

export const createMercadoPagoPreference = async (
  items: CartItem[],
  options: {
    acceptChanges?: boolean;
    externalReferencePrefix?: string;
    courseId?: string | null;
    organizerId?: string | null;
  } = {}
) => {
  const token = getCurrentToken();
  const currentUser = getCurrentUser();

  if (!token || !currentUser) {
    throw new CheckoutAuthRequiredError();
  }

  const response = await fetch(`${getApiBaseUrl()}/payments/create-preference`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      items: buildCheckoutItems(items),
      external_reference: `${options.externalReferencePrefix || 'peekpass_checkout'}_${Date.now()}`,
      organizerId: options.organizerId || items[0]?.organizerId || null,
      courseId: options.courseId || items[0]?.courseId || items[0]?.eventId || null,
      acceptChanges: Boolean(options.acceptChanges)
    })
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (isAuthError(response.status, result)) {
      clearUserSession();
      throw new CheckoutAuthRequiredError(result?.message || result?.error, result);
    }

    if (response.status === 409 && result?.code === 'CHECKOUT_REVALIDATION_REQUIRED') {
      throw new CheckoutRevalidationError(result);
    }

    throw new Error(getCheckoutErrorMessage(result));
  }

  return result;
};

export const applyCheckoutUpdatesToCart = (items: CartItem[], result: any): CartItem[] => {
  const currentLines = Array.isArray(result?.currentLines) ? result.currentLines : [];
  const changes = Array.isArray(result?.changes) ? result.changes : [];
  if (!currentLines.length) {
    if (!changes.length) return items;
    const removedKeys = new Set(changes.map((change: any) => `${change.eventId || ''}:${change.ticketTierId || change.previousTicketTierId || ''}`));
    return items.filter((item) => {
      const eventId = item.eventId || item.courseId || item.id.split(':')[0];
      const tierId = item.ticketTierId || (item.id.includes(':') ? item.id.split(':').slice(1).join(':') : 'general');
      return !removedKeys.has(`${eventId}:${tierId}`);
    });
  }

  const linesByKey = new Map<string, any>(currentLines.map((line: any) => [`${line.eventId}:${line.ticketTierId}`, line]));

  return items
    .map((item) => {
      const eventId = item.eventId || item.courseId || item.id.split(':')[0];
      const tierId = item.ticketTierId || (item.id.includes(':') ? item.id.split(':').slice(1).join(':') : 'general');
      const line = linesByKey.get(`${eventId}:${tierId}`);
      if (!line) return item;

      return {
        ...item,
        id: `${line.eventId}:${line.ticketTierId}`,
        eventId: line.eventId,
        courseId: line.eventId,
        ticketTierId: line.ticketTierId,
        ticketTierName: line.ticketTierName,
        price: Number(line.unitPrice),
        originalPrice: Number(line.unitPrice),
        quantity: Number(line.quantity ?? item.quantity ?? 1),
        currency: line.currency || item.currency,
        serviceFeeRate: Number(line.serviceFeeRate || item.serviceFeeRate || 5)
      };
    })
    .filter((item) => (item.quantity || 0) > 0);
};

export const summarizeCheckoutChanges = (result: any) => {
  const changes = Array.isArray(result?.changes) ? result.changes : [];
  if (!changes.length) return result?.message || 'La información de la compra cambió.';

  return changes.map((change: any) => change.message || change.reason).filter(Boolean).join('\n');
};
